import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import { activate2FA, enable2FA, get2FAQr } from '../api/auth/Auth'

// QR image size in pixels. Adjust this value to change the displayed QR size.
const QR_SIZE_PX = 220;

// Minimal base64url decode for JWT payload
function decodeJwt(token) {
  try {
    const payload = token.split('.')[1]
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/')
    const json = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    )
    return JSON.parse(json)
  } catch (e) {
    return null
  }
}

function formatMMSS(totalSeconds) {
  const s = Math.max(0, totalSeconds)
  const m = Math.floor(s / 60)
  const sec = s % 60
  return `${m.toString().padStart(2, '0')}:${sec.toString().padStart(2, '0')}`
}

function TwoFASetup() {
  const navigate = useNavigate()
  const [secret, setSecret] = useState('')
  const [qrUrl, setQrUrl] = useState('')
  const [code, setCode] = useState('')
  const [remaining, setRemaining] = useState(0)
  const [expired, setExpired] = useState(false)

  const setupToken = useMemo(() => sessionStorage.getItem('twofa_token'), [])
  const setupType = useMemo(() => sessionStorage.getItem('twofa_type'), [])

  // Initialize countdown based on JWT exp
  useEffect(() => {
    if (!setupToken || setupType !== 'setup') {
      navigate('/login')
      return
    }
    const payload = decodeJwt(setupToken)
    if (!payload?.exp) {
      navigate('/login')
      return
    }
    const tick = () => {
      const now = Date.now()
      const expMs = payload.exp * 1000
      const diff = Math.ceil((expMs - now) / 1000)
      setRemaining(diff)
      if (diff <= 0) {
        setExpired(true)
      }
    }
    // Prime and start interval
    tick()
    const id = setInterval(tick, 1000)
    return () => clearInterval(id)
  }, [navigate, setupToken, setupType])

  // Fetch secret and QR on mount
  useEffect(() => {
    if (!setupToken || setupType !== 'setup') return
    const payload = decodeJwt(setupToken)
    if (!payload?.exp || payload.exp * 1000 <= Date.now()) {
      setExpired(true)
      return
    }
    ;(async () => {
      try {
        const resp = await enable2FA(setupToken)
        setSecret(resp?.data?.secret)
        const qrResp = await get2FAQr(setupToken)
        const url = URL.createObjectURL(qrResp.data)
        setQrUrl(url)
      } catch (e) {
        toast.error('Unable to start 2FA setup')
      }
    })()
  }, [setupToken, setupType])

  const onSubmit = async (e) => {
    e.preventDefault()
    if (expired) {
      toast.error('The QR/session has expired. Please login again to restart.')
      return
    }
    const token = sessionStorage.getItem('twofa_token')
    try {
      await activate2FA(token, code)
      toast.success('2FA activated. Please login again to verify.')
      sessionStorage.removeItem('twofa_token')
      sessionStorage.removeItem('twofa_type')
      navigate('/login')
    } catch (e) {
      toast.error('Invalid code, try again')
    }
  }

  const handleRestart = () => {
    sessionStorage.removeItem('twofa_token')
    sessionStorage.removeItem('twofa_type')
    navigate('/login')
  }

  const qrClasses = `mx-auto border border-gray-700 rounded ${expired ? 'filter blur-sm grayscale' : ''}`

  return (
    <div className="font-neue-machina flex min-h-screen bg-darkmode text-white items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="w-full max-w-md space-y-6">
        <h2 className="text-center text-2xl font-medium">Two-Factor Authentication Setup</h2>
        <p className="text-center text-sm text-gray-300">Scan the QR with your Authenticator app or enter the secret manually, then enter the 6-digit code.</p>

        {/* Countdown */}
        {!expired ? (
          <p className="text-center text-xs text-gray-400">QR valid for: <span className="font-mono">{formatMMSS(remaining)}</span></p>
        ) : (
          <p className="text-center text-sm text-red-400">QR expired. Please go back to login to generate a new one.</p>
        )}

        {qrUrl && (
          <img
            src={qrUrl}
            alt="2FA QR"
            className={qrClasses}
            style={{ width: `${QR_SIZE_PX}px`, height: `${QR_SIZE_PX}px`, objectFit: 'contain' }}
          />
        )}

        <form onSubmit={onSubmit} className="space-y-4">
          <input
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            pattern="[0-9]{6}"
            maxLength={6}
            value={code}
            onChange={(e) => {
              const v = e.target.value.replace(/[^0-9]/g, '').slice(0, 6)
              setCode(v)
            }}
            placeholder="Enter 6-digit code"
            className={`w-full p-3 rounded border focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary ${expired ? 'bg-gray-300 text-gray-500 border-gray-300 cursor-not-allowed' : 'bg-white border-gray-300 text-black placeholder:text-gray-500 caret-black'}`}
            required
            disabled={expired}
          />
          {!expired ? (
            <button type="submit" className="w-full bg-primary hover:bg-primary/80 rounded p-2">Activate 2FA</button>
          ) : (
            <button type="button" onClick={handleRestart} className="w-full bg-gray-600 hover:bg-gray-500 rounded p-2">Back to login</button>
          )}
        </form>
      </div>
    </div>
  )
}

export default TwoFASetup
